import { create } from 'zustand'
import { z } from 'zod'

const STORAGE_KEY = 'sidebar:collapsed'
// Below the desktop breakpoint the sidebar is shown as a collapsed icon rail
// instead of being hidden. Matches the `min-width: 1136px` used in the CSS.
const COMPACT_QUERY = '(max-width: 1135px)'

// Zod boundary: localStorage is untrusted input (rules/main.md).
const collapsedSchema = z.boolean()

const readCollapsed = (): boolean => {
    try {
        return collapsedSchema.parse(JSON.parse(localStorage.getItem(STORAGE_KEY) ?? 'false'))
    } catch {
        return false
    }
}

const persistCollapsed = (collapsed: boolean): void => {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(collapsed))
    } catch {
        // Ignore write failures (e.g. private mode / storage disabled).
    }
}

const hasMatchMedia = (): boolean => typeof window !== 'undefined' && typeof window.matchMedia === 'function'

const matchesCompact = (): boolean => (hasMatchMedia() ? window.matchMedia(COMPACT_QUERY).matches : false)

interface SidebarStore {
    /** User preference toggled via the collapse bar (desktop only). */
    isCollapsed: boolean
    /** True when the viewport is below the desktop breakpoint. */
    isCompact: boolean
    toggle: () => void
    setCollapsed: (collapsed: boolean) => void
}

export const useSidebarStore = create<SidebarStore>((set) => ({
    isCollapsed: readCollapsed(),
    isCompact: matchesCompact(),
    toggle: () =>
        set((state) => {
            const next = !state.isCollapsed
            persistCollapsed(next)
            return { isCollapsed: next }
        }),
    setCollapsed: (collapsed: boolean) => {
        persistCollapsed(collapsed)
        set({ isCollapsed: collapsed })
    }
}))

// Keep isCompact in sync with the viewport (single module-level subscription).
if (hasMatchMedia()) {
    window
        .matchMedia(COMPACT_QUERY)
        .addEventListener('change', (event) => useSidebarStore.setState({ isCompact: event.matches }))
}

/** Effective collapsed state: user preference OR forced by a compact viewport. */
export const useIsSidebarCollapsed = (): boolean => useSidebarStore((state) => state.isCollapsed || state.isCompact)
